import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Wrench } from "lucide-react";
import { toast } from "sonner";

const Maintenance = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ facility: "", issue: "", priority: "medium" });

  const load = async () => {
    try { const { data } = await api.get("/maintenance"); setList(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/maintenance", form);
      setList((l) => [data, ...l]); setOpen(false); setForm({ facility: "", issue: "", priority: "medium" });
      toast.success("Report submitted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="maintenance-page">
      <PageHeader
        overline="Facility"
        title="Maintenance Reports"
        description="Report broken equipment, leaking faucets, electrical issues and other facility concerns."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="new-maint-btn"><Plus className="w-4 h-4 mr-1.5" /> New Report</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Maintenance Report</DialogTitle></DialogHeader>
              <div className="grid gap-4 mt-2">
                <Fld label="Facility / Location"><Input value={form.facility} onChange={(e) => setForm({ ...form, facility: e.target.value })} data-testid="maint-facility" placeholder="Block B — Grade 5 Classroom" /></Fld>
                <Fld label="Issue"><Textarea rows={4} value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} data-testid="maint-issue" /></Fld>
                <Fld label="Priority">
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </Fld>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="maint-submit">Submit</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {list.length === 0 ? <EmptyState message="No maintenance reports yet." /> : (
        <div className="grid gap-3">
          {list.map(m => (
            <Card key={m.id} className="p-5 border-[#E2E8F0]" data-testid={`maint-${m.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#1B2C5A]/5 text-[#1B2C5A] grid place-items-center"><Wrench className="w-5 h-5" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-lg text-[#1B2C5A]">{m.facility}</h3>
                    <span className={"text-xs px-2 py-0.5 rounded-full border " + (
                      m.priority === "urgent" ? "bg-red-50 text-red-700 border-red-200" :
                      m.priority === "high"   ? "bg-orange-50 text-orange-700 border-orange-200" :
                      m.priority === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                                "bg-slate-50 text-slate-700 border-slate-200")}>{m.priority}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#FDFBF7] border border-[#E2E8F0]">{m.status}</span>
                  </div>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">{m.issue}</p>
                  <div className="text-xs text-[#475569] mt-3">By {m.raised_by_name} · {new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default Maintenance;
